import RoleService from "@/services/api/role";
import { handleResponse } from "@/services/axios";
import axios from "axios";
import {
  createAsyncThunk,
  createSlice,
  isFulfilled,
  isPending,
  isRejected,
} from "@reduxjs/toolkit";


interface LoadingState {
  roles: boolean;
}
interface RoleState {
  roles: any[];
  permissions: any;
  isFetching: boolean;
  error: string | null;
  loading: LoadingState;
}

const initialState: RoleState = {
  roles: [],
  permissions: null,
  isFetching: false,
  error: null,
  loading: {
    roles: false,
  }
};

export const getRoles = createAsyncThunk(
  "role/getRoles",
  async (_, { rejectWithValue }) => {
    try {
      const response = await RoleService.getRoles();
      return handleResponse(response, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(err?.Message || "Failed to fetch roles");
    }
  }
);

export const deleteRole = createAsyncThunk(
  "role/deleteRole",
  async (roleId: number, { rejectWithValue }) => {
    try {
      const response = await RoleService.deleteRole(roleId);
      return handleResponse(response, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(err?.Message || "Failed to delete role");
    }
  }
);

export const addRole = createAsyncThunk(
  "role/addRole",
  async (role: any, { rejectWithValue }) => {
    try {
      const response = await RoleService.addRole(role);
      return handleResponse(response, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(err?.Message || "Failed to add role");
    }
  }
);

export const updateRole = createAsyncThunk(
  "role/updateRole",
  async (role: any, { rejectWithValue }) => {
    try {
      const response = await RoleService.updateRole(role);
      return handleResponse(response, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(err?.Message || "Failed to update role");
    }
  }
);

export const getPermissions = createAsyncThunk(
  "role/getPermissions",
  async (roleId: any, { rejectWithValue }) => {
    try {
      const response = await RoleService.getPermissions(roleId);
      return handleResponse(response, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(
        err?.Message || "Failed to fetch role permissions"
      );
    }
  }
);

export const updatePermission = createAsyncThunk(
  "role/updatePermission",
  async (
    arg: { data: any; signal?: AbortSignal },
    { rejectWithValue }
  ) => {
    try {
      const response = await RoleService.updatePermission(arg.data, arg.signal);
      return handleResponse(response, rejectWithValue);
    } catch (err: any) {
      // aborted request not a real failure.
      if (
        axios.isCancel?.(err) ||
        err?.code === "ERR_CANCELED" ||
        err?.name === "CanceledError"
      ) {
        return rejectWithValue({ canceled: true });
      }
      return rejectWithValue(err?.Message || "Failed to update permissions");
    }
  }
);

export const getAuditLogsById = createAsyncThunk(
  "role/getAuditLogsById",
  async (roleId: any, { rejectWithValue }) => {
    try {
      const response = await RoleService.getAuditLogsById(roleId);
      return handleResponse(response, rejectWithValue);
    } catch (err: any) {
      return rejectWithValue(err?.Message || "Failed to fetch audit logs");
    }
  }
);
const roleSlice = createSlice({
  name: "role",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // specific updates
    builder
      .addCase(getRoles.pending, (state) => {
        state.loading.roles = true;
      })
      .addCase(getRoles.fulfilled, (state, action) => {
        state.loading.roles = false;
        state.roles = action.payload;
      })
      .addCase(getRoles.rejected, (state, action) => {
        state.loading.roles = false;
        state.error = action.payload as string;
      })
      .addCase(addRole.fulfilled, (state, action) => {
        state.roles.push(action.payload);
      })
      .addCase(updateRole.fulfilled, (state, action) => {
        const matchedIndex = state.roles.findIndex(
          (r) => r.Id === action.payload.Id
        );
        if (matchedIndex !== -1) state.roles[matchedIndex] = action.payload;
      })
      .addCase(deleteRole.fulfilled, (state, action) => {
        state.roles = state.roles.filter((r) => r.Id !== action.payload.Id);
      })
      .addCase(getPermissions.fulfilled, (state, action) => {
        state.permissions = action.payload;
      });

    // Generic loading & error
    builder
      .addMatcher(isPending, (state) => {
        state.isFetching = true;
        state.error = null;
      })
      .addMatcher(isRejected, (state, action) => {
        state.isFetching = false;
        state.error = action.payload as string;
      })
      .addMatcher(isFulfilled, (state) => {
        state.isFetching = false;
      });
  },
});

export default roleSlice.reducer;
